import React, { useRef, useEffect, useState, useMemo } from "react";
import { CardContent } from "@/components/ui/card";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "highcharts/modules/accessibility";
import "highcharts/modules/exporting";
import "highcharts/modules/export-data";
import "highcharts/modules/sankey";
import { useDashboardData } from "@/services/dashboardDataService";
import "./widgets.css";
import { Skeleton } from "@/components/ui/skeleton";
import { cleanDateAndTimeObject } from "@shared/renderDate";

// Type for data flow items (matching the backend collector's output)
type DataFlowItem = [string, string, number];

interface DataFlowData {
  data: DataFlowItem[];
  metadata?: {
    lastUpdated?: string;
  };
}

// This will be populated from the dashboard data service
const useDataFlowData = () => {
  const { data, isLoading, error } = useDashboardData("data-flow");
  
  // Memoize the parsed data to prevent unnecessary recalculations
  const parsedData = useMemo(() => {
    try {
      if (!data?.data) return [];
      
      // Handle both array of objects and array of arrays for backward compatibility
      if (Array.isArray(data.data)) {
        // If first item is an object, convert to array format
        if (data.data.length > 0 && data.data[0] !== null && typeof data.data[0] === 'object' && !Array.isArray(data.data[0])) {
          return data.data.map((item: any) => [
            item.from || '',
            item.to || '',
            typeof item.weight === 'number' ? item.weight : 1
          ]);
        }
        // If it's already in array format, return as is
        return data.data as DataFlowItem[];
      }
      return [];
    } catch (e) {
      console.error('Error parsing data flow data:', e);
      return [];
    }
  }, [data]);
  
  return {
    data: parsedData,
    isLoading,
    error,
    lastUpdated: data?.lastUpdated
  };
};

// Pastel colors for the sankey diagram
const pastelColors = [
  '#FDCB9E', '#BEE3DB', '#F5CAC3', '#D7E5A1', '#F3E1A5', '#C7CEEA', '#E2A9A1'
];

const getChartOptions = (data: DataFlowItem[]): Highcharts.Options => ({
  chart: {
    height: 400,
    type: "sankey",
    backgroundColor: "transparent",
  },
  title: {
    text: undefined
  },
  credits: {
    enabled: false
  },
  exporting: {
    enabled: false
  },
  plotOptions: {
    sankey: {
      nodeWidth: 20,
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '12px',
          textOutline: 'none',
          color: 'var(--foreground)'
        },
        nodeFormat: '{point.name}',
        format: '' // Clear the default format
      },
      nodePadding: 10,
    }
  },
  series: [{
    keys: ["from", "to", "weight"],
    data: data,
    type: "sankey",
    name: "Data Flow Diagram",
    colorByPoint: true,
    colors: pastelColors,
    tooltip: {
      nodeFormat: '<b>{point.name}</b>: {point.sum}',
      headerFormat: '<span style="font-size: 13px">Data Flow</span><br/>',
      pointFormat: '<b>{point.fromNode.name}</b> → <b>{point.toNode.name}</b>: {point.weight}',
      useHTML: true
    },
  } as Highcharts.SeriesSankeyOptions]
});

const DataFlowDiagram: React.FC = () => {
  const { data, isLoading, error, lastUpdated } = useDataFlowData();
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  
  // Get chart options without memoization to prevent dependency issues
  const chartOptions = getChartOptions(data || []);

  if (isLoading || !data) {
    return (
      <CardContent className="h-full w-full flex flex-col items-center justify-center" style={{ containerType: 'inline-size' }}>
        <Skeleton className="h-6 w-1/3 mb-4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6 mt-2" />
      </CardContent>
    );
  }
  
  // Don't render the chart until we have data
  if (!data || data.length === 0) {
    return (
      <CardContent className="h-full w-full flex flex-col items-center justify-center" style={{ containerType: 'inline-size' }}>
        <div className="text-muted-foreground">No data available</div>
      </CardContent>
    );
  }

  if (error) {
    return (
      <CardContent className="h-full w-full flex flex-col items-center justify-center" style={{ containerType: 'inline-size' }}>
        <div className="text-center text-red-500">
          Error loading data flow diagram. Please try again later.
        </div>
      </CardContent>
    );
  }

  return (
    <CardContent className="h-full w-full flex flex-col items-center justify-center p-0" style={{ containerType: 'inline-size' }}>
      <div className="w-full flex justify-between items-center mb-2 px-4 pt-4">
        <h3 className="text-lg font-semibold">Data Flow Diagram</h3>
        {lastUpdated && (
          <span className="text-xs text-muted-foreground">
            Updated: {cleanDateAndTimeObject(lastUpdated).toLocaleTimeString()}
          </span>
        )}
      </div>
      <div className="h-full w-full flex-1 p-0" style={{ minHeight: 0, minWidth: 0 }}>
        <HighchartsReact
          ref={chartRef}
          highcharts={Highcharts}
          options={chartOptions}
          containerProps={{ 
            style: { 
              width: '100%', 
              height: '100%',
              minHeight: '200px'
            } 
          }}
          immutable={true}
        />
      </div>
    </CardContent>
  );
};

export default DataFlowDiagram;
